import React, { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
};

const FORMAS_PAGO = [
  { valor: "01", label: "Sin utilización del sistema financiero" },
  { valor: "15", label: "Compensación de deudas" },
  { valor: "16", label: "Tarjeta de débito" },
  { valor: "17", label: "Dinero electrónico" },
  { valor: "18", label: "Tarjeta prepago" },
  { valor: "19", label: "Tarjeta de crédito" },
  { valor: "20", label: "Otros con utilización del sistema financiero" },
];

export default function LiquidacionCompras() {
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const [proveedor, setProveedor] = useState({ nombre: "", ruc: "", direccion: "", email: "" });
  const [detalles, setDetalles] = useState([{ descripcion: "", cantidad: 1, precioUnitario: 0, iva: "15%" }]);
  const [formaPago, setFormaPago] = useState("01");
  const [firmaPassword, setFirmaPassword] = useState("");

  // Retenciones opcionales - desactivadas por defecto
  const [aplicarRetenciones, setAplicarRetenciones] = useState(false);
  const [retPorcentajeRenta, setRetPorcentajeRenta] = useState(1.75);
  const [retPorcentajeIVA, setRetPorcentajeIVA] = useState(100);

  // Tipo de gasto / cuenta contable
  const [cuentasGasto, setCuentasGasto] = useState([]);
  const [cuentaGastoSeleccionada, setCuentaGastoSeleccionada] = useState("");
  const [afectaInventario, setAfectaInventario] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/liquidacion-compras`, { headers: getAuthHeaders() });
      const data = await res.json();
      setLiquidaciones(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  const cargarCuentas = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/liquidacion-compras/plan-cuentas`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        // Filtrar cuentas de gasto y costo que aceptan movimiento
        const gastos = (data.data.gastos || []).filter(c => c.acepta_movimiento !== false);
        setCuentasGasto(gastos);
      }
    } catch (e) { console.error("Error cargando cuentas:", e); }
  }, []);

  useEffect(() => { cargar(); cargarCuentas(); }, [cargar, cargarCuentas]);

  const actualizarDetalle = (idx, campo, valor) => {
    const copia = [...detalles];
    copia[idx] = { ...copia[idx], [campo]: valor };
    setDetalles(copia);
  };

  const calcularTotales = () => {
    let subtotal15 = 0, subtotal0 = 0;
    detalles.forEach(d => {
      const sub = parseFloat(d.cantidad || 0) * parseFloat(d.precioUnitario || 0);
      if (d.iva === "15%") subtotal15 += sub; else subtotal0 += sub;
    });
    const iva15 = subtotal15 * 0.15;
    const totalSinImpuestos = subtotal15 + subtotal0;
    const importeTotal = totalSinImpuestos + iva15;
    const retRenta = aplicarRetenciones ? parseFloat((totalSinImpuestos * retPorcentajeRenta / 100).toFixed(2)) : 0;
    const retIVA = aplicarRetenciones ? parseFloat((iva15 * retPorcentajeIVA / 100).toFixed(2)) : 0;
    return { totalSinImpuestos, subtotal15, subtotal0, iva15, importeTotal, retRenta, retIVA, valorAPagar: importeTotal - retRenta - retIVA };
  };

  const [successPopup, setSuccessPopup] = useState(null); // { id, numero, claveAcceso }

  const crear = async () => {
    if (!proveedor.nombre) { setError("Ingrese el nombre del proveedor"); return; }
    if (!proveedor.ruc) { setError("Ingrese el RUC o cédula del proveedor"); return; }
    if (detalles.length === 0 || !detalles.some(d => d.descripcion && d.precioUnitario > 0)) {
      setError("Agregue al menos un detalle con descripción y precio"); return;
    }
    setLoading(true); setError(""); setMsg("");
    try {
      const t = calcularTotales();
      const body = {
        proveedor: { ...proveedor, razonSocial: proveedor.nombre, identificacion: proveedor.ruc },
        detalles: detalles.filter(d => d.descripcion && d.precioUnitario > 0).map(d => ({
          ...d, precioTotalSinImpuesto: d.cantidad * parseFloat(d.precioUnitario || 0)
        })),
        totales: t, formaPago, firmaPassword,
        aplicarRetenciones,
        retencionRenta: aplicarRetenciones ? { porcentaje: retPorcentajeRenta, valor: t.retRenta, codigo: "345" } : null,
        retencionIVA: aplicarRetenciones ? { porcentaje: retPorcentajeIVA, valor: t.retIVA, codigo: "8" } : null,
        cuentaGasto: cuentaGastoSeleccionada || null,
        afectaInventario,
        cuentasContables: cuentaGastoSeleccionada ? { compraGasto: cuentaGastoSeleccionada } : null
      };
      const res = await fetch(`${API}/api/liquidacion-compras`, { method: "POST", headers: getAuthHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        setProveedor({ nombre: "", ruc: "", direccion: "", email: "" });
        setDetalles([{ descripcion: "", cantidad: 1, precioUnitario: 0, iva: "15%" }]);
        setCuentaGastoSeleccionada("");
        setAfectaInventario(false);
        setSuccessPopup({
          id: data.liquidacion?.id,
          numero: data.liquidacion?.numero || "",
          claveAcceso: data.claveAcceso || ""
        });
        cargar();
      } else { setError(data.error || "Error al generar la liquidación"); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const descargarPDF = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/liquidacion-compras/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Error generando PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { setError("Error al generar el PDF"); }
  };

  const t = calcularTotales();
  const estadoBadge = (estado) => {
    const colores = { AUTORIZADO: "#4caf50", PENDIENTE: "#ff9800", NO_AUTORIZADO: "#f44336", ANULADA: "#9e9e9e" };
    return { background: colores[estado] || "#9e9e9e", color: "#fff", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 };
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Liquidación de Compras</h2>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
            Para compras a proveedores informales (personas naturales no obligadas a facturar)
          </p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setError(""); setMsg(""); }}
          style={{ ...btnPrimary, fontSize: 14, padding: "10px 20px" }}>
          {showForm ? "✕ Cancelar" : "+ Nueva Liquidación"}
        </button>
      </div>

      {msg && <div style={alertSuccess}>{msg}</div>}
      {error && <div style={alertError}>{error} <button onClick={() => setError("")} style={{ float: "right", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}

      {/* FORMULARIO */}
      {showForm && (
        <div style={cardStyle}>
          {/* Proveedor */}
          <div style={sectionCard}>
            <h4 style={sectionTitle}>👤 Datos del Proveedor</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={labelStyle}>Nombre / Razón Social *</label>
                <input value={proveedor.nombre} onChange={e => setProveedor({ ...proveedor, nombre: e.target.value })}
                  style={inputStyle} placeholder="Nombre del proveedor" />
              </div>
              <div>
                <label style={labelStyle}>RUC / Cédula *</label>
                <input value={proveedor.ruc} onChange={e => setProveedor({ ...proveedor, ruc: e.target.value })}
                  style={inputStyle} placeholder="Número de identificación" />
              </div>
              <div>
                <label style={labelStyle}>Dirección</label>
                <input value={proveedor.direccion} onChange={e => setProveedor({ ...proveedor, direccion: e.target.value })}
                  style={inputStyle} placeholder="Dirección (opcional)" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={proveedor.email} onChange={e => setProveedor({ ...proveedor, email: e.target.value })}
                  style={inputStyle} placeholder="Email (opcional)" type="email" />
              </div>
            </div>
          </div>

          {/* Detalles */}
          <div style={sectionCard}>
            <h4 style={sectionTitle}>🛒 Detalle de la Compra</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    <th style={{ ...thStyle, width: "50%" }}>Descripción</th>
                    <th style={{ ...thStyle, width: "12%", textAlign: "center" }}>Cant.</th>
                    <th style={{ ...thStyle, width: "15%", textAlign: "center" }}>P. Unitario</th>
                    <th style={{ ...thStyle, width: "10%", textAlign: "center" }}>IVA</th>
                    <th style={{ ...thStyle, width: "13%", textAlign: "right" }}>Subtotal</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {detalles.map((d, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: 6 }}>
                        <input value={d.descripcion} onChange={e => actualizarDetalle(i, "descripcion", e.target.value)}
                          placeholder="Descripción del bien o servicio" style={{ ...inputStyle, margin: 0 }} />
                      </td>
                      <td style={{ padding: 6 }}>
                        <input type="number" value={d.cantidad} onChange={e => actualizarDetalle(i, "cantidad", parseFloat(e.target.value) || 0)}
                          style={{ ...inputStyle, margin: 0, textAlign: "center" }} min="1" />
                      </td>
                      <td style={{ padding: 6 }}>
                        <input type="number" value={d.precioUnitario} onChange={e => actualizarDetalle(i, "precioUnitario", parseFloat(e.target.value) || 0)}
                          style={{ ...inputStyle, margin: 0, textAlign: "right" }} step="0.01" min="0" />
                      </td>
                      <td style={{ padding: 6 }}>
                        <select value={d.iva} onChange={e => actualizarDetalle(i, "iva", e.target.value)}
                          style={{ ...inputStyle, margin: 0, textAlign: "center" }}>
                          <option value="15%">15%</option>
                          <option value="0%">0%</option>
                        </select>
                      </td>
                      <td style={{ padding: 6, textAlign: "right", fontWeight: 600, fontSize: 14 }}>
                        ${(d.cantidad * parseFloat(d.precioUnitario || 0)).toFixed(2)}
                      </td>
                      <td style={{ padding: 6 }}>
                        {detalles.length > 1 && (
                          <button onClick={() => setDetalles(detalles.filter((_, j) => j !== i))}
                            style={{ background: "#ffebee", color: "#c62828", border: "none", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setDetalles([...detalles, { descripcion: "", cantidad: 1, precioUnitario: 0, iva: "15%" }])}
              style={{ ...btnSecondary, marginTop: 12 }}>+ Agregar línea</button>
          </div>

          {/* Configuración: Tipo de Gasto + Inventario + Forma de Pago */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={sectionCard}>
              <h4 style={sectionTitle}>⚙️ Configuración</h4>

              {/* Tipo de Gasto / Cuenta Contable */}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Tipo de Gasto / Cuenta Contable</label>
                <select value={cuentaGastoSeleccionada} onChange={e => setCuentaGastoSeleccionada(e.target.value)} style={inputStyle}>
                  <option value="">-- Seleccionar cuenta de gasto --</option>
                  {cuentasGasto.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} - {c.nombre}</option>
                  ))}
                </select>
                <span style={{ fontSize: 11, color: "#666" }}>Cuenta donde se registrará el gasto de esta compra</span>
              </div>

              {/* Afecta Inventario */}
              <div style={{ marginBottom: 14, background: "#f5f5f5", padding: 12, borderRadius: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  <input type="checkbox" checked={afectaInventario} onChange={e => setAfectaInventario(e.target.checked)}
                    style={{ width: 16, height: 16 }} />
                  Afecta al Inventario
                </label>
                <span style={{ fontSize: 11, color: "#666", marginLeft: 24, display: "block", marginTop: 4 }}>
                  Marque si esta compra ingresa productos al stock (materiales, mercadería)
                </span>
              </div>

              {/* Forma de Pago */}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Forma de Pago</label>
                <select value={formaPago} onChange={e => setFormaPago(e.target.value)} style={inputStyle}>
                  {FORMAS_PAGO.map(fp => (
                    <option key={fp.valor} value={fp.valor}>{fp.label}</option>
                  ))}
                </select>
              </div>

              {/* Contraseña Firma */}
              <div>
                <label style={labelStyle}>Contraseña de Firma (opcional)</label>
                <input type="password" value={firmaPassword} onChange={e => setFirmaPassword(e.target.value)}
                  style={inputStyle} placeholder="Si no ingresa, usa la guardada" />
              </div>
            </div>

            {/* Columna derecha: Retenciones + Resumen */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Retenciones opcionales */}
              <div style={{ ...sectionCard, background: aplicarRetenciones ? "#fff8e1" : "#f5f5f5", border: aplicarRetenciones ? "1px solid #ffe082" : "1px solid #eee" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                    <input type="checkbox" checked={aplicarRetenciones} onChange={e => setAplicarRetenciones(e.target.checked)}
                      style={{ width: 16, height: 16 }} />
                    Aplicar Retenciones
                  </label>
                </div>
                {aplicarRetenciones ? (
                  <>
                    <p style={{ fontSize: 11, color: "#666", margin: "0 0 10px" }}>
                      Se retiene al proveedor. Solo aplica si su empresa es agente de retención.
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 12 }}>% Ret. Renta</label>
                        <input type="number" value={retPorcentajeRenta} onChange={e => setRetPorcentajeRenta(parseFloat(e.target.value) || 0)}
                          style={inputStyle} step="0.01" min="0" max="100" />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 12 }}>% Ret. IVA</label>
                        <input type="number" value={retPorcentajeIVA} onChange={e => setRetPorcentajeIVA(parseFloat(e.target.value) || 0)}
                          style={inputStyle} step="1" min="0" max="100" />
                      </div>
                    </div>
                  </>
                ) : (
                  <p style={{ fontSize: 12, color: "#888", margin: 0 }}>
                    No se aplicarán retenciones. El valor a pagar será el total de la compra.
                  </p>
                )}
              </div>

              {/* Resumen */}
              <div style={{ ...sectionCard, background: "#f8fffe", border: "1px solid #b2dfdb", flex: 1 }}>
                <h4 style={{ ...sectionTitle, color: "#00695c" }}>💰 Resumen</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={totalsRow}>
                    <span>Subtotal sin impuestos:</span>
                    <span style={{ fontWeight: 600 }}>${t.totalSinImpuestos.toFixed(2)}</span>
                  </div>
                  <div style={totalsRow}>
                    <span>IVA 15%:</span>
                    <span style={{ fontWeight: 600 }}>${t.iva15.toFixed(2)}</span>
                  </div>
                  <div style={{ ...totalsRow, borderTop: "1px solid #e0e0e0", paddingTop: 6 }}>
                    <span>Total:</span>
                    <span style={{ fontWeight: 600 }}>${t.importeTotal.toFixed(2)}</span>
                  </div>
                  {aplicarRetenciones && (
                    <>
                      <div style={{ ...totalsRow, color: "#e65100" }}>
                        <span>Ret. Renta {retPorcentajeRenta}%:</span>
                        <span>-${t.retRenta.toFixed(2)}</span>
                      </div>
                      <div style={{ ...totalsRow, color: "#e65100" }}>
                        <span>Ret. IVA {retPorcentajeIVA}%:</span>
                        <span>-${t.retIVA.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div style={{ ...totalsRow, borderTop: "2px solid #00695c", paddingTop: 8, marginTop: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#00695c" }}>VALOR A PAGAR:</span>
                    <span style={{ fontSize: 17, fontWeight: 700, color: "#00695c" }}>${t.valorAPagar.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botón Generar */}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button onClick={crear} disabled={loading}
              style={{ ...btnPrimary, padding: "14px 40px", fontSize: 16, background: "#2e7d32" }}>
              {loading ? "⏳ Procesando..." : "🚀 Generar y Enviar al SRI"}
            </button>
          </div>
        </div>
      )}

      {/* LISTADO */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={thStyle}>Número</th>
              <th style={thStyle}>Fecha</th>
              <th style={thStyle}>Proveedor</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
              <th style={{ ...thStyle, textAlign: "right" }}>A Pagar</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Estado</th>
              <th style={{ ...thStyle, textAlign: "center" }}>PDF</th>
            </tr>
          </thead>
          <tbody>
            {liquidaciones.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#999" }}>
                {loading ? "Cargando..." : "No hay liquidaciones registradas"}
              </td></tr>
            ) : liquidaciones.map(lc => (
              <tr key={lc.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={tdStyle}><strong>{lc.numero}</strong></td>
                <td style={tdStyle}>{lc.fecha_emision}</td>
                <td style={tdStyle}>{lc.proveedor?.nombre || lc.proveedor?.razonSocial}</td>
                <td style={{ ...tdStyle, textAlign: "right" }}>${parseFloat(lc.totales?.importeTotal || 0).toFixed(2)}</td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>${parseFloat(lc.valor_a_pagar || 0).toFixed(2)}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <span style={estadoBadge(lc.estado_sri)}>{lc.estado_sri}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <button onClick={() => descargarPDF(lc.id)} style={btnPDF} title="Ver PDF">
                    📄
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* POPUP DE ÉXITO */}
      {successPopup && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, maxWidth: 420, width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 20, color: "#2e7d32" }}>Liquidación Generada</h3>
            <p style={{ color: "#666", fontSize: 14, margin: "0 0 8px" }}>
              No. <strong>{successPopup.numero}</strong>
            </p>
            <p style={{ color: "#888", fontSize: 12, margin: "0 0 20px" }}>
              Enviada al SRI para autorización. Puede descargar el PDF desde el listado una vez autorizada.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => { setSuccessPopup(null); }}
                style={{ ...btnPrimary, padding: "12px 28px", fontSize: 14 }}>
                Volver a Liquidaciones
              </button>
              {successPopup.id && (
                <button onClick={() => { descargarPDF(successPopup.id); setSuccessPopup(null); }}
                  style={{ ...btnSecondary, padding: "12px 28px", fontSize: 14 }}>
                  📄 Ver PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Estilos
const cardStyle = { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 24, marginBottom: 24 };
const sectionCard = { background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: 16, marginBottom: 16 };
const sectionTitle = { margin: "0 0 12px 0", fontSize: 15, color: "#333" };
const btnPrimary = { background: "#1976d2", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnSecondary = { background: "#f5f5f5", color: "#333", border: "1px solid #ddd", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13 };
const btnPDF = { background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 16 };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, boxSizing: "border-box" };
const labelStyle = { display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13, color: "#333" };
const thStyle = { padding: "12px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#666", textTransform: "uppercase" };
const tdStyle = { padding: "12px 14px", fontSize: 13 };
const totalsRow = { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 };
const alertSuccess = { background: "#e8f5e9", padding: 14, borderRadius: 8, marginBottom: 16, color: "#2e7d32", border: "1px solid #a5d6a7" };
const alertError = { background: "#ffebee", padding: 14, borderRadius: 8, marginBottom: 16, color: "#c62828", border: "1px solid #ef9a9a" };
